/* eslint-disable max-len */
import React, { useMemo } from 'react';
import cn from 'classnames';
import { TextShield } from 'app/components/ui/Elements/DishImageShield/imgV2/text/TextShield';
import { useCartMutationInBasket } from 'app/views/Basket/hooks';
import { makeFlatSection, makePrimetiveSection } from 'app/views/Basket/BasketListArea';
import {
    gql, useFragment, useQuery,
} from '@apollo/client';
import { useToastExecuter } from 'app/hooks/useToastExecuter';
import { useDishPrice } from 'app/hooks/dish';
import { useDishDetails } from 'app/components/MobileDishDetails';
import { analyticService } from 'global/services';
import { subscriptionTypeVar } from 'app/apollo/reaction';
import { devLog } from '@apollo/client/invariantErrorCodes';
import css from './basketTopDishCard.module.scss';


import { BASKET_PAGE_CART_SECTIONS_WITH_DISHES, BASKET_PAGE_DELIVERY_CART_SECTIONS_WITH_DISHES } from '../../../../graphql/basketListArea.graphql';

import { topDishes } from './topDishesRecord';

import DividerIcon from './divider.component.svg';
import PlusIcon from './plus.component.svg';
import MinusIcon from './minus.component.svg';


interface IBasketTopDishCardProps {
    id: string;
}
interface IBasketTopDishCardButtonProps {
    id: string;
    handleShowToast: () => void
}

const BasketTopDishCardButton = ({ id, handleShowToast }: IBasketTopDishCardButtonProps) => {
    const isDeliveryBasket = window.location.pathname.includes('app/delivery');
    const deliveryId = window.location.pathname.split('/').find((e) => Boolean(e) && !Number.isNaN(Number(e)));
    const {
        isCartUpdating,
        mutateItemInCart,
    } = useCartMutationInBasket(isDeliveryBasket ? { delivery_Id: deliveryId } : undefined);

    /* DA */
    const { data, loading } = useQuery<any>(isDeliveryBasket
        ? BASKET_PAGE_DELIVERY_CART_SECTIONS_WITH_DISHES
        : BASKET_PAGE_CART_SECTIONS_WITH_DISHES,
    {
        fetchPolicy: 'cache-first',
        variables: isDeliveryBasket ? { delivery_id: deliveryId } : {},
    });

    const menuDish = useFragment({
        fragment: gql`
            fragment TOP_CARD_MENU_DISH on menuDish {
                id,
                price,
                portions,
                title
                caption
                category_id
            }
        `,
        from: {
            __ref: `menuDish:${id}`,
        },
    });

    /* SELECTORS */
    const price = useDishPrice({ id });

    const sections = useMemo(
        () => (isDeliveryBasket ? data?.deliveryCart?.sections : data?.cart?.sections),
        [isDeliveryBasket, data],
    );

    const primetiveSection = useMemo(
        () => makePrimetiveSection(sections || []),
        [sections],
    );

    const flatSection = useMemo(
        () => makeFlatSection(sections),
        [sections],
    );

    const inBasket = useMemo(
        () => primetiveSection.includes(`cartItem:${id}`),
        [primetiveSection, id],
    );

    const portionStep = useMemo(() => {
        const [portion] = menuDish?.data?.portions || [0];
        return portion;
    }, [menuDish?.data?.portions],
    );

    const portionCount = useMemo(
        () => {
            const item = flatSection.find((elm: any) => (elm?.dish_id === id));
            return item?.portions || 0;
        },
        [flatSection, id],
    );

    const disabled = useMemo(
        () => (loading || isCartUpdating),
        [isCartUpdating, loading],
    );

    const disabledForPlus = useMemo(
        () => {
            // eslint-disable-next-line no-unsafe-optional-chaining
            const R = (menuDish?.data?.portions?.length - 1) || -1;
            return R < 0
                ? false
                : menuDish?.data?.portions[R] <= portionCount;
        },
        [menuDish?.data?.portions, portionCount],
    );

    const uiPortionText = useMemo(
        () => (portionCount || portionStep),
        [portionCount, portionStep],
    );

    /* HANDLERS */

    const addToBasket = (event: any, action: string) => {
        const portions = action === 'plus'
            ? (portionCount + portionStep)
            : (portionCount - portionStep);
        mutateItemInCart({
            dishes: [{ dish_id: id, portions }],
            // @ts-expect-error
            optimistic: false,
        });
        analyticService.push({
            eventName: 'add_to_cart',
            dishes: [{
                ...menuDish?.data,
                categoryId: menuDish?.data?.category_id,
                portion: portions,
            }],
            source: '/basket',
            subscriptionType: subscriptionTypeVar(),
            typeOfSet: 'regular',
        });
    };


    /* STL */
    const topDishCardButtonRoot = cn({
        [css.topDishCardButtonRoot]: true,
        [css.inBasket]: inBasket,
    });

    const centralItemRootClasses = cn({
        [css.centralItemRootClasses]: true,
        [css.inBasket]: inBasket,
    });

    const leftItemClasses = cn({
        [css.cornerItemRootClasses]: true,
        [css.left]: true,
    });

    const rigthItemClasses = cn({
        [css.cornerItemRootClasses]: true,
        [css.right]: true,
    });

    return (
        <div
            className={topDishCardButtonRoot}
            onClick={(e) => e.stopPropagation()}
            role="none"
        >
            <div className={css.topDishCardButtonWrapper}>
                {/* MINUS */}
                {inBasket && (
                    <button
                        className={leftItemClasses}
                        onClick={(e) => {
                            addToBasket(e, 'minus');
                        }}
                        disabled={disabled}
                        type="button"
                    >
                        <div
                            className={css.minusIcon}
                        >
                            <MinusIcon />
                        </div>
                    </button>
                )}
                {/*  */}
                <button
                    onClick={(e) => {
                        addToBasket(e, 'plus');
                        handleShowToast();
                    }}
                    type="button"
                    disabled={disabled || inBasket}
                    className={centralItemRootClasses}
                >
                    <div className={css.step}>
                        {uiPortionText}
                    </div>
                    <div className={css.divider}>
                        <DividerIcon />
                    </div>
                    <div className={css.price}>
                        {price}
                    </div>
                </button>
                {/* PLUS */}
                {inBasket && (
                    <button
                        className={rigthItemClasses}
                        onClick={(e) => addToBasket(e, 'plus')}
                        disabled={disabled || disabledForPlus}
                        type="button"
                    >
                        <div className={css.plusIcon}>
                            <PlusIcon />
                        </div>
                    </button>
                )}
            </div>
        </div>
    );
};

export const BasketTopDishCard = ({ id }: IBasketTopDishCardProps) => {
    const { title: { firstLine, secondLine }, description } = topDishes[id];
    const [handleShowToast] = useToastExecuter({ customEventName: 'executeBasketToastNotification' });
    const { openDishDetails } = useDishDetails();

    const handleSendAnalytics = () => {
        analyticService.push({
            eventName: 'Upsell_Click',
            upsell_type: 'promo',
            banner_name: 'legend',
            source_name: 'basket',
        });
    };

    const backgroundClasses = cn({
        [css.basketTopDishCardRoot]: true,
        [css[`top_${id}`]]: true,
    });

    const titleClasses = cn({
        [css.basketTopDishCardTitle]: true,
        [css[`top_${id}`]]: true,
    });

    const descriptionClasses = cn({
        [css.basketTopDishCardDescription]: true,
        [css[`top_${id}`]]: true,
    });
    return (
        <div
            onClick={() => {
                openDishDetails(id);
                handleSendAnalytics();
            }}
            className={css.basketTopDishRootWrapper}
            role="none"
        >
            <div className={backgroundClasses}>
                <div className={css.basketTopDishContentContainer}>
                    <div className={css.basketTopDishShieldWrapper}>
                        <TextShield
                            name="legend"
                            position="static"
                        />
                    </div>
                    <div className={titleClasses}>
                        {firstLine}
                        <br />
                        {secondLine}
                    </div>
                    <div className={descriptionClasses}>
                        {description}
                    </div>
                    <BasketTopDishCardButton
                        id={id}
                        handleShowToast={handleShowToast}
                    />
                </div>
            </div>
        </div>
    );
};
